import * as React from 'react';

import { cn } from '../../../lib/utils';
import { Skeleton } from '../../ui/skeleton';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GridSkeletonProps {
  /** Additional class names for the grid container. */
  className?: string;
  /** Number of columns in the grid (default: 2). */
  columns?: number;
  /** Number of placeholder cards to render (default: 4). */
  count?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMN_CLASSES: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic loading placeholder for card grids. Renders `count` skeleton cards
 * arranged in `columns` columns. Each card mimics a header (avatar + two text
 * lines), a body block, and a footer button block.
 */
export function GridSkeleton({ className, columns = 2, count = 4 }: GridSkeletonProps): React.JSX.Element {
  return (
    <div
      aria-label="Loading"
      className={cn('grid gap-4', COLUMN_CLASSES[columns] ?? 'grid-cols-2', className)}
      role="status"
    >
      {Array.from({ length: count }).map((_, index) => (
        <div className="flex flex-col gap-3 rounded-xl border p-4" key={index}>
          <div className="flex items-start gap-3">
            <Skeleton className="h-10 w-10 rounded-md" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  );
}
